import React, { JSX, FC, useState, useEffect } from "react";
import axios from "axios";
import { handleError } from "utils/handleError";
import * as ProductsAPI from "api/products";
import { useSelector } from "react-redux";
import { PropertyType } from "products/types";
import { AppStateType } from "redux/reducers/mainReducer";

import { AutoComplete, Button, Form, Input } from "antd";
import { ReactComponent as TrashIcon } from "assets/icons/trash-icon.svg";
import useCancelableDebounce from "hooks/useCancelableDebounce";
import { SelectOptionType } from "app/types";

type UpdateCharcArg = FocusEvent | { target: any };

interface IPropertyItemProps {
  key: number;
  name: number;
  remove: (index: (number | number[])) => void;
  handleFocus: () => void;
  updateCharc: (arg: UpdateCharcArg) => void;
  isDisabled: boolean;
}

const PropertyItem: FC<IPropertyItemProps> = ({
  key,
  name,
  remove,
  handleFocus,
  updateCharc,
  isDisabled,
}): JSX.Element => {
  const properties: PropertyType[] = useSelector(
    (state: AppStateType) => state.productsSlice
  ).properties.list;

  const [options, setOptions] = useState<SelectOptionType[]>([]);
  const [value, setValue] = useState<string>("");

  useEffect(() => {
    if (properties?.length) {
      setOptions(properties.map(({ name }) => ({ value: name, label: name })));
    }
  }, [properties]);

  const handleChange = (newValue: string): void => setValue(newValue);

  const { debouncedFunction: searchProperties } = useCancelableDebounce((params, token) => {
    ProductsAPI.getProductProperties(params, token)
      .then(({ data }) => {
        setOptions(data?.results?.map(({ name }) => ({ value: name, label: name })));
      })
      .catch((error: any) => {
        if (!axios.isCancel(error)) {
          handleError("Ошибка при получении списка свойств.", error);
        }
      });
  }, 600);

  const handleSearch = (value: string): void => searchProperties({ name: value });

  const handleBlur = ({ target }): void => {
    updateCharc(target ?? {});

    const defaultOptions: SelectOptionType[] = properties
      ?.filter(({ name }) => name !== value)
      ?.map(({ name }) => ({ value: name, label: name }));

    setOptions([
      ...(value ? [{ value, label: value }] : []),
      ...defaultOptions,
    ]);
  };

  const handleDelete = (): void => {
    remove(name);
    updateCharc({ target: "properties" });
  };

  return (
    <div className="property-row flex gap-2 mb-4" key={key}>
      <Form.Item
        name={[name, "propertyName"]}
        rules={[{ required: true, message: "Введите название свойства" }]}
        style={{ flex: 1, }}
        className="m-0"
      >
        <AutoComplete
          size="large"
          allowClear
          placeholder="Введите название свойства"
          value={value}
          filterOption={false}
          options={options}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onSearch={handleSearch}
          disabled={isDisabled}
        />
      </Form.Item>
      <Form.Item
        name={[name, "propertyValue"]}
        rules={[{ required: true, message: "Введите значение свойства" }]}
        style={{ flex: 1 }}
        className="m-0"
      >
        <Input
          onFocus={handleFocus}
          onBlur={updateCharc}
          size="large"
          placeholder="Введите значение свойства"
          disabled={isDisabled}
          showCount
          maxLength={255}
        />
      </Form.Item>
      <Button
        type="default"
        size="large"
        className="items-center text-indigo-600"
        onClick={handleDelete}
        disabled={isDisabled}
      >
        <TrashIcon />
      </Button>
    </div>
  );
};

export default PropertyItem;
